import './MessagingChannelPreview.css';
import {
  ChannelPreviewUIComponentProps,
  ChatContextValue,
  DefaultStreamChatGenerics,
  useChatContext,
} from 'stream-chat-react';
import AvatarChannelView from '../AvatarChannelView/AvatarChannelView';

import { useEffect, useState,useCallback } from 'react';
import type { MouseEventHandler } from 'react';
import type { Channel, ChannelMemberResponse, StreamChat } from 'stream-chat';
import { ChatPageType } from '../../enums/ChatPageType';
import ChatMessage from '../ChatMessage/ChatMessage';
import { useChannelContext } from '../ChannelContext/ChannelContext';
import { useBlockUnblockContext } from '../../context/BlockUnblock';
import WebInterop from '../../implementation/WebInterop';
import Interop from "../../interfaces/Interop";
import { ChannelDataModel } from "../../models/ChannelDataModel";
import HybridWebView from '../../hybridWebView/HybridWebView';
import CustomPopupDialog from '../CustomPopupDialog/CustomPopupDialog';
import NativeInterop from '../../implementation/NativeInterop';

/**
 * HELPER: getTimeStamp
 * Formats date string to 12-hour time format (e.g., "2:30 PM")
 */
const getTimeStamp = (dateString: string) => {
  const date = new Date(dateString);
  let hours = date.getHours();
  let minutes: string | number = date.getMinutes();
  let half = 'AM';

  if (hours > 12) {
    hours -= 12;
    half = 'PM';
  }

  if (hours === 0) hours = 12; // Handle midnight case
  if (minutes.toString().length === 1) {
    minutes = `0${minutes}`; // Ensure two-digit minutes
  }

  return `${hours}:${minutes} ${half}`;
};

/**
 * HELPER: formatMessageDate
 * 
 * Formats message timestamp for display
 * 
 * FORMATS:
 * - Today: Time (e.g., "2:30 PM")
 * - Yesterday: "Yesterday"
 * - Older: Date (e.g., "12/10/2024")
 */
const formatMessageDate = (dateString: string) => {
  if (!dateString) return '';
  const messageDate = new Date(dateString);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);

  if (messageDate.toDateString() === today.toDateString()) {
    return getTimeStamp(dateString);
  } else if (messageDate.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  } else {
    return messageDate.toLocaleDateString();
  }
};

/**
 * HELPER: getChannelName
 * 
 * Gets display name for DM channel
 * Excludes current user from member list
 * 
 * RETURNS:
 * - Other user's name (DM)
 * - "unname channel" (fallback)
 */
const getChannelName = (members: ChannelMemberResponse[], currentUserId?: string) => {
  const defaultName = 'unname channel';

  // If no members or only one member, return that member's name (if it's not the current user)
  if (!members.length || members.length === 1) {
    const member = members[0];
    // Check if this member is the current user - if so, return default name
    if (member?.user?.id === currentUserId) {
      return defaultName;
    }
    return member?.user?.name;
  }

  // For multiple members, filter out the current user and return the first other member's name
  const otherMembers = members.filter(member => member?.user?.id !== currentUserId);
  
  if (otherMembers.length === 0) {
    return defaultName;
  }
  
  // Return only the first other member's name (excluding current user)
  return otherMembers[0]?.user?.name;
};

/**
 * HELPER: getBlockedUsers
 * 
 * Checks if current user blocked the other DM participant
 * 
 * RETURNS:
 * - true: User is blocked by current user
 * - false: User not blocked or error occurred
 */
const getBlockedUsers = async (members: ChannelMemberResponse[], client: StreamChat<DefaultStreamChatGenerics>) => {
  try {
    const users = await client.getBlockedUsers();

    if (users.blocks.length > 0) {
      const isUserBlocked = users.blocks.some((blockedUser) =>
        blockedUser.user_id === client.userID && blockedUser.blocked_user_id === members[0]?.user?.id
      );

      return isUserBlocked;
    } else {
      return false;
    }
  } catch (error) {
    console.error("Error while fetching blocked users:", error);
    return false;
  }
};

/**
 * HELPER: update
 * 
 * Unfreezes a blocked channel
 * Sets frozen: false to allow messaging again
 */
async function update(channel: any) {

  const response = await channel.updatePartial({ set: { frozen: false } });

  return response.channel;
}


type MessagingChannelPreviewProps = ChannelPreviewUIComponentProps & {
  channel: Channel;
  onClick: MouseEventHandler;
  setActiveChannel?: ChatContextValue['setActiveChannel'];
  languageCode: any,
};

const MessagingChannelPreview = (props: MessagingChannelPreviewProps) => {
  var { channel, setActiveChannel, onClick, latestMessage, languageCode } = props;
  const { channel: activeChannel, client } = useChatContext<DefaultStreamChatGenerics>();
  
  /**
   * STATE MANAGEMENT
   * - isTyping: Whether other user is typing
   * - isAlreadyMember: Whether user is member of group channel
   * - isblocked: Whether channel is blocked
   * - isMuted: Whether channel is muted
   * - blockedChannels: Map of channel IDs to block status
   * - showPopup: Whether to show unblock confirmation
   * - isUserBlock: Whether current user was blocked by other
   */
  const [isTyping, setTyping] = useState<boolean>(false);
  const [isAlreadyMember, setAlreadyMember] = useState<boolean>(false);
  const [isblocked, setBlocked] = useState<boolean>(false);
  const [isMuted, setMuted] = useState<boolean>(false);
  const members = Object.values(channel.state.members).filter(
    ({ user }) => user?.id !== client.userID,
  );
  const { selectedChannel, setSelectedChannel } = useChannelContext();
  const { isUserBlocked, toggleBlock } = useBlockUnblockContext();
  const [blockedChannels, setBlockedChannels] = useState<{ [key: string]: boolean }>({});
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [isUserBlock, setIsUserBlocked] = useState<boolean>(false);
  let interop: Interop;

  const maxMessagesShowCount = 99;
  
  /**
   * HELPER: getChannelAutoJoin
   * Returns whether channel is configured for auto-join
   */
  const getChannelAutoJoin = (channel:Channel): boolean => {
      const rawDetails = channel.data?.details;
      if (typeof rawDetails === "string") {
          try {
              const details = JSON.parse(rawDetails);
              return Boolean(details?.IsAutoJoin);
          } catch {
              return false;
          }
      }
      return false;
  };
// Helper function to determine membership status
  const getIsAlreadyMember = useCallback(() => {
    const isChannelAutoJoin = getChannelAutoJoin(channel);
    if (channel.type === "public_with_membership") {
      if (client.user?.role === "moderator") {
        return channel.type === "public_with_membership";
      }
      if(isChannelAutoJoin)
        return true;
      
      return !!channel.state.members[client.userID!];
    }
    return channel.type === "messaging" || 
           channel.type === "public_without_membership" || 
           channel.type === "public_without_membership_watchlive";
  }, [channel, client]);

  // Update membership status when channel changes
  useEffect(() => {
    setAlreadyMember(getIsAlreadyMember());
  }, [getIsAlreadyMember]);

  // Listen for membership changes
  useEffect(() => {
    const checkMembership = () => {
      setAlreadyMember(getIsAlreadyMember());
    };

    // Set up event listeners for membership changes
    channel.on('member.added', checkMembership);
    channel.on('member.removed', checkMembership);
    channel.on('member.updated', checkMembership);

    // Clean up listeners
    return () => {
      channel.off('member.added', checkMembership);
      channel.off('member.removed', checkMembership);
      channel.off('member.updated', checkMembership);
    };
  }, [channel, getIsAlreadyMember]);
  
  // useEffect for updating the block state based on user block status
  useEffect(() => {
    if (channel.type === 'messaging') {
      const userId = members[0]?.user?.id;
      if (userId) {
          let blockedState=true;
          if (setActiveChannel) {
            blockedState = channel.data?.frozen!!;
          }
          else {
            blockedState = channel.data?.frozen || isUserBlocked(userId);
          }
          setBlockedChannels(prevState => ({
            ...prevState,
            [channel.id!!]: blockedState, // Ensure blocked state is updated correctly per channel
          }));

      }
    }
  }, [isUserBlocked]);

  // useEffect to update the blocked state when blockedChannels change
  useEffect(() => {
      setBlocked(blockedChannels[channel.id!!] ?? false); // Ensure the state is updated whenever blockedChannels changes
    if(isblocked){
      getBlockedUsers(members, client).then(blockedUser=>  {
        if(blockedUser) {
          setIsUserBlocked(false);
        }
        else{
          setIsUserBlocked(true);
        }
      });
    }
  }, [ blockedChannels[channel.id!!],channel.id]);

  // Initial setup useEffect
  useEffect(() => {

    if (HybridWebView.IsWebView()) {
      interop = new NativeInterop();
    }
    else {
      interop = new WebInterop();
    }
    channel!.on('all', (event: any) => {
      if (event.type === 'all') {
        let value = false;
        if (event.text === "user blocked") {
          if (event.user.id === members[0].user_id!!) {
            setIsUserBlocked(true);
          }
          value = true
        }
        setBlocked(value);
      }
    });


    channel.on((event: any) => {
      if (channel.type == "public_with_membership") {

        if (event.type === "member.updated") {

          var eventData = (event.member) as any;
          if (eventData != null && eventData != undefined && eventData.user_id != undefined) {
            setAlreadyMember(true);
          }
        }
        if (channel.muteStatus().muted) {
          var data = channel.state.read[client.userID!!]
          if (data.unread_messages > 0) {
            setMuted(true)
          }
        }

      }
    
    });

    // Set initial membership status
    setAlreadyMember(getIsAlreadyMember());

    if (channel.type == "messaging") {
      if (channel.data?.frozen === true) {
        setBlocked(true)
        toggleBlock(members[0]?.user?.id!);
      }
    }
  }, []);
  // Determine if this channel is the active one (needs to happen before unread calculations)
  const isActive = activeChannel?.id === channel.id;

  let unreadCount = 0;
  var data = channel.state.read[client.userID!!]
  if (isActive) {
    // Clear the badge when the channel already matches the active chat view
    unreadCount = 0;
  } else if (data?.unread_messages > 0) {
    unreadCount = data.unread_messages;
  } else {
    unreadCount = 0;
  }

  // Listen for typing events
  channel.on('typing.start', event => {
    if (event.user?.id != client?.user?.id)
      setTyping(true)
  })

  // Listen for stop typing events
  channel.on('typing.stop', event => {
    if (event.user?.id != client?.user?.id)
      setTyping(false)
  })

  // Handle unblock confirmation (Confirm button click)
  const handleUnblockConfirmation = async (e: React.MouseEvent) => {
    e.stopPropagation();  // Prevent the click event from bubbling up
    const userId = members[0]?.user?.id;
    if (!userId) return;
    // If the user is currently blocked
    const isCurrentlyBlocked = blockedChannels[channel.id!!] || channel.data?.frozen;
    if (isCurrentlyBlocked) {
      // Unblock the user and update channel state
      client.unBlockUser(userId).then(() => {
        // Triggering an update to unfreeze the channel
        update(channel).then(channeldata => {
          const blockedState = isUserBlocked(userId);

          setBlockedChannels(prevState => ({
            ...prevState,
            [channel.id!!]: false,
          }));

          if (setActiveChannel) {
            toggleBlock(members[0]?.user?.id!);
          }
          setBlocked(false);
          setShowPopup(false); // Close the popup after confirmation
          channel = channeldata;
        })
      });
      await channel.sendEvent({ type: 'all', text: 'user unblocked' });
    }
  };
  // Handle cancellation (Cancel button click)
  const handlePopupClose = (e: React.MouseEvent) => {
    e.stopPropagation();  // Prevent the click event from bubbling up
    setShowPopup(false); // Close the popup without doing anything
  };

  // Helper function to check and join channel if needed
  const checkAndJoinChannel = async () => {
    if (channel.type !== 'public_with_membership') return;
    const userId = client.userID!;
    const isMeInChannel = !!channel.state.members[userId];
    if (!isMeInChannel) {
      try{
        await channel.addMembers([{ user_id: client?.user?.id!!, channel_role: "channel_member" }]);
      }
      catch(error){
        console.error("Error joining channel:", error);
      }
    }
  };

  return (
    <div
      className={`channel-preview__container ${isActive ? 'active' : 'inactive'}`}
      onClick={async (e) => {
        e.preventDefault();
        if (!isblocked) {
          await checkAndJoinChannel();
          onClick(e);
          setActiveChannel?.(channel);
          setSelectedChannel(channel); // Set the selected channel in context
        } else {
          getBlockedUsers(members, client).then(blockedUser => {
            if (blockedUser)
              setShowPopup(true);
          });
        }
      }}
    >
      <AvatarChannelView data={channel} pageType={ChatPageType.ChannelListing} />
<div className='channel-preview__content-wrapper'>
  <div className='channel-preview__content-top'>
    <p className='channel-preview__content-name'>
      {channel.data?.name || getChannelName(members)}
    </p>

    {isAlreadyMember ? (
      <p className='channel-preview__content-time'>
        {formatMessageDate(channel.state.last_message_at?.toString()!!)}
      </p>
    ) : (
      <ChatMessage
        isBlocked={isblocked}
        isAlreadyMember={isAlreadyMember}
        isTyping={isTyping}
        latestMessage={latestMessage}
        languageCode={languageCode}
        isUserBlock={isUserBlock}
      />
    )}
  </div>

  <div className='channel-preview__content-container'>
  <div
    className={
      isAlreadyMember
        ? unreadCount || isTyping || isMuted
          ? 'channel-preview__content-message-unread'
          : 'channel-preview__content-message'
        : 'channel-preview__content-message'
    }
  >
    {isAlreadyMember && (
  <ChatMessage
    isBlocked={isblocked}
    isAlreadyMember={isAlreadyMember}
    isTyping={isTyping}
    latestMessage={latestMessage}
    languageCode={languageCode}
    isUserBlock={isUserBlock}
  />
)}
  </div>

  { isAlreadyMember && unreadCount > 0 && !isblocked && (
    <div className="channel-preview__unread-badge">
      <span className="unread-count">
        {unreadCount <= maxMessagesShowCount ? unreadCount : maxMessagesShowCount+"+"}
      </span>        
    </div>
  )}

</div>
</div>

      {showPopup && (
        <CustomPopupDialog
          languageCode={languageCode}
          onConfirm={(e) => handleUnblockConfirmation(e)}
          onCancel={(e) => handlePopupClose(e)}
        />
      )}
    </div>
  );
};

export default MessagingChannelPreview;

